import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useFeedback } from '../../hooks/useFeedback';
import { useAuth } from '../../contexts/auth';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [type, setType] = useState('');
  const [comment, setComment] = useState('');
  const [wantReply, setWantReply] = useState(false);
  const { submitFeedback, isLoading } = useFeedback();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rating || !type || !comment) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    try {
      await submitFeedback({
        rating,
        type,
        comment,
        wantReply,
        userId: user.id,
        userEmail: user.email,
        userName: user.nome || user.nickname
      });
      
      toast.success('Feedback enviado com sucesso!');
      onClose();
      
      // Limpar o formulário
      setRating(0);
      setType('');
      setComment('');
      setWantReply(false);
    } catch (error) {
      toast.error('Erro ao enviar feedback. Tente novamente mais tarde.');
    }
  };

  const StarRating = () => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          className="focus:outline-none"
        >
          {star <= rating ? (
            <StarIcon className="h-8 w-8 text-yellow-400" />
          ) : (
            <StarOutlineIcon className="h-8 w-8 text-gray-300 hover:text-yellow-400" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
          <Dialog.Title className="text-xl font-semibold text-gray-900 mb-4">
            Sua opinião é importante!
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Como você avalia nossa aplicação?
              </label>
              <StarRating />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de feedback
              </label>
              <div className="space-y-2">
                {[
                  { value: 'compliment', label: 'Elogio' },
                  { value: 'bug', label: 'Reportar erro' },
                  { value: 'suggestion', label: 'Sugestão' },
                  { value: 'criticism', label: 'Crítica' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="type"
                      value={option.value}
                      checked={type === option.value}
                      onChange={(e) => setType(e.target.value)}
                      className="text-purple-600"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seu feedback
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                rows={4}
                placeholder="Descreva seu feedback aqui..."
              />
            </div>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={wantReply}
                onChange={(e) => setWantReply(e.target.checked)}
                className="text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">
                Desejo receber resposta por e-mail
              </span>
            </label>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 
                  rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando...' : 'Enviar feedback'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default FeedbackModal;
